import { FC, useState } from 'react';
import { toast } from 'sonner';

import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { Loader } from '@new/common/ui/Loader/Loader';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { getErrorMessage } from '@new/lib/getErrorMessage';

import { useEvaluation } from '../hooks/useEvaluation';
import { InformationWizard } from '../components/wizard/InformationWizard';
import { InfoSummaryPanel } from '../components/page/InfoSummaryPanel';
import { CreditStatsBar } from '../components/page/CreditStatsBar';
import { TranscriptTable } from '../components/page/TranscriptTable';
import { TranscriptDetailsModal } from '../components/details/TranscriptDetailsModal';
import { EditTranscriptModal } from '../components/details/EditTranscriptModal';
import { AddTranscriptModal } from '../components/add/AddTranscriptModal';
import { GoToCourseBuilderCard } from '../components/page/GoToCourseBuilderCard';
import type { TranscriptBreakdown } from '@new/features/transcripts/shared/types';

export const StudentTranscriptsPage: FC = () => {
  const {
    evaluation,
    isLoading,
    refetch,
    removeTranscript,
    markTranscriptReady,
    isMarkingReady,
    updateTranscript,
    isUpdatingTranscript,
  } = useEvaluation();

  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [addMethod, setAddMethod] = useState<'file' | 'manual' | null>(null);
  const [selected, setSelected] = useState<TranscriptBreakdown | null>(null);
  const [toDelete, setToDelete] = useState<TranscriptBreakdown | null>(null);
  const [toMarkReady, setToMarkReady] = useState<TranscriptBreakdown | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader />
      </div>
    );
  }

  const infoComplete = evaluation?.priorEducationStatus != null;
  const showWizard = !infoComplete || isEditingInfo;

  if (showWizard) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-8">
        <InformationWizard
          evaluation={evaluation}
          onDone={() => {
            setIsEditingInfo(false);
            void refetch();
          }}
          onCancel={infoComplete ? () => setIsEditingInfo(false) : undefined}
        />
      </div>
    );
  }

  const transcripts = evaluation!.transcripts ?? [];

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await removeTranscript(toDelete.id);
      setToDelete(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleMarkReady = async () => {
    if (!toMarkReady) return;
    try {
      await markTranscriptReady(toMarkReady.id);
      setToMarkReady(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const onToggleOfficialRequested = async (transcript: TranscriptBreakdown, requested: boolean) => {
    try {
      await updateTranscript(transcript.id, {
        institution: transcript.institution,
        unofficialCredits: transcript.unofficialCredits,
        officialTranscriptRequested: requested,
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-8">
      <PageHeader
        title="Transfer credits"
        description={
          <>
            Upload your transcript(s), or manually add your transcript information for provisional
            evaluation.
            <span className="ml-0.5 text-(--danger-D700)!">*</span>
          </>
        }
      />

      <div className="flex w-full flex-col gap-8 rounded-[9px] border border-solid border-(--neutral-black-NB300) bg-(--neutral-white-NW1000) p-8">
        <CreditStatsBar
          transcriptCount={transcripts.length}
          unofficialTotal={evaluation!.unofficialTotal}
          officialTotal={evaluation!.officialTotal}
        />

        <InfoSummaryPanel evaluation={evaluation!} canEdit onEdit={() => setIsEditingInfo(true)} />

        <TranscriptTable
          transcripts={transcripts}
          canEdit
          isMarkingReady={isMarkingReady}
          isUpdatingTranscript={isUpdatingTranscript}
          onUpload={() => setAddMethod('file')}
          onAddManual={() => setAddMethod('manual')}
          onOpen={setSelected}
          onDelete={setToDelete}
          onMarkReady={setToMarkReady}
          onToggleOfficialRequested={onToggleOfficialRequested}
        />

        <div className="border-t border-(--neutral-black-NB100) pt-6 text-center">
          <p className="[font-size:var(--fs-p3)]! text-(--neutral-black-NB700)!">
            Add each transcript and click <strong>Ready</strong> when it is complete. You can keep
            adding more at any time.
          </p>
        </div>
      </div>
      <GoToCourseBuilderCard />
      <p className="m-0! text-(--neutral-black-NB1000)">
        <span className="ml-0.5 text-(--danger-D700)!">*</span> This is an unofficial evaluation,
        but will give you a good idea for the number of credits that will transfer to Newlane.
        Official transcripts must be sent directly to Newlane from the issuing institution.{' '}
        <a
          href="https://newlane.edu/transferring-credits-toward-an-aa-or-ba-degree/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-(--primary-O1000)!"
        >
          See instructions for requesting official transcripts here.
        </a>
      </p>

      {selected && selected.status === 'draft' ? (
        <EditTranscriptModal
          key={selected.id}
          transcript={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            void refetch();
          }}
        />
      ) : (
        <TranscriptDetailsModal transcript={selected} onClose={() => setSelected(null)} />
      )}

      {addMethod ? (
        <AddTranscriptModal
          key={addMethod}
          isOpen
          method={addMethod}
          onClose={() => setAddMethod(null)}
          onAdded={() => {
            setAddMethod(null);
            void refetch();
          }}
        />
      ) : null}

      <ConfirmationModal
        isOpen={!!toDelete}
        title="Remove transcript?"
        description="This will remove the transcript from your evaluation."
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setToDelete(null)}
      />

      <ConfirmationModal
        isOpen={!!toMarkReady}
        title="Mark transcript ready?"
        description="Make sure the transcript information you entered is complete and accurate. Your provisional transfer credit estimate is based on this information, so the more accurate it is, the more closely your official evaluation is likely to match."
        confirmText="Mark ready"
        cancelText="Cancel"
        isLoading={isMarkingReady}
        onConfirm={handleMarkReady}
        onClose={() => setToMarkReady(null)}
      />
    </div>
  );
};
